import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Image as ImageIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  Container,
  IconButton,
  Paper,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { getCarouselImages } from "../utils/carouselImages.ts";

const Section = styled(Box)<{ $dark: boolean }>(({ theme, $dark }) => ({
  padding: theme.spacing(8, 0),
  backgroundColor: $dark ? theme.palette.background.default : "#f8f4f7",
}));

const Header = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(5),
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  fontWeight: 600,
  color: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
}));

const Wrapper = styled(Box)({
  position: "relative",
  maxWidth: 760,
  margin: "0 auto",
});

const Frame = styled(Paper)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: theme.spacing(2),
  backgroundColor:
    theme.palette.mode === "dark" ? "#2a2a2a" : theme.palette.secondary.light,
  boxShadow: theme.shadows[2],
  height: 460,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  [theme.breakpoints.down("md")]: { height: 380 },
  [theme.breakpoints.down("sm")]: { height: 315 },
}));

const BlurredBackdrop = styled("img")({
  position: "absolute",
  inset: "-7%",
  width: "114%",
  height: "114%",
  objectFit: "cover",
  filter: "blur(24px)",
  opacity: 0.28,
  transform: "scale(1.04)",
});

const Media = styled("img")({
  position: "relative",
  zIndex: 1,
  width: "100%",
  height: "100%",
  objectFit: "contain",
  display: "block",
});

const NavButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "rgba(255,255,255,.92)",
  color: theme.palette.text.primary,
  boxShadow: theme.shadows[2],
  zIndex: 2,
  "&:hover": {
    backgroundColor: "#fff",
  },
}));

const PlayButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: 14,
  top: 14,
  backgroundColor: "rgba(255,255,255,.92)",
  color: theme.palette.text.primary,
  boxShadow: theme.shadows[2],
  zIndex: 2,
  "&:hover": { backgroundColor: "#fff" },
}));

const Dots = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: theme.spacing(0.8),
  marginTop: theme.spacing(2.25),
}));

const Dot = styled(Box)<{ $active: boolean }>(({ theme, $active }) => ({
  width: 10,
  height: 10,
  borderRadius: "50%",
  backgroundColor: $active ? theme.palette.primary.main : theme.palette.grey[400],
}));

export const SmartCarousel: React.FC = () => {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const [images, setImages] = useState<string[]>([]);
  const [index, setIndex] = useState(0);
  const [auto, setAuto] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    getCarouselImages()
      .then((items) => {
        setImages(items);
        setLoading(false);
      })
      .catch(() => {
        setError("De fotogalerij kon niet worden geladen.");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!auto || images.length < 2) return;
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % images.length);
    }, 4500);
    return () => window.clearInterval(timer);
  }, [auto, images.length]);

  const next = () =>
    setIndex((i) => (images.length ? (i + 1) % images.length : 0));
  const previous = () =>
    setIndex((i) =>
      images.length ? (i - 1 + images.length) % images.length : 0
    );

  const visibleDots = () => {
    if (images.length <= 7) return images.map((_, i) => i);
    const start = Math.max(0, Math.min(index - 3, images.length - 7));
    return Array.from({ length: 7 }, (_, i) => start + i);
  };

  return (
    <Section id="gallery" component="section" $dark={dark}>
      <Container maxWidth="lg">
        <Header>
          <Title variant="h4" component="h2">
            <ImageIcon />
            Fotogalerij
          </Title>
          <Typography color="text.secondary">
            Bekijk de mooiste momenten van Pippaya Hondenopvang
          </Typography>
        </Header>

        {error && <Alert severity="error">{error}</Alert>}

        {loading ? (
          <Wrapper>
            <Skeleton variant="rounded" height={460} />
          </Wrapper>
        ) : images.length ? (
          <Wrapper>
            <Frame>
              <BlurredBackdrop
                src={images[index]}
                alt=""
                aria-hidden="true"
              />
              <Media src={images[index]} alt={`Pippaya foto ${index + 1}`} />

              {images.length > 1 && (
                <>
                  <NavButton
                    aria-label="Vorige foto"
                    onClick={previous}
                    sx={{ left: 14 }}
                  >
                    <ChevronLeftIcon />
                  </NavButton>

                  <NavButton
                    aria-label="Volgende foto"
                    onClick={next}
                    sx={{ right: 14 }}
                  >
                    <ChevronRightIcon />
                  </NavButton>

                  <PlayButton
                    aria-label={auto ? "Diavoorstelling pauzeren" : "Diavoorstelling starten"}
                    onClick={() => setAuto((v) => !v)}
                  >
                    {auto ? <PauseIcon /> : <PlayIcon />}
                  </PlayButton>
                </>
              )}
            </Frame>

            <Dots aria-hidden="true">
              {visibleDots().map((i) => (
                <Dot key={i} $active={i === index} />
              ))}
            </Dots>

            <Box sx={{ textAlign: "center", mt: 1.5 }}>
              <Chip
                size="small"
                variant="outlined"
                color="primary"
                icon={<ImageIcon fontSize="small" />}
                label={`${index + 1} / ${images.length}`}
              />
            </Box>
          </Wrapper>
        ) : (
          <Typography align="center" color="text.secondary">
            Er staan nog geen foto's in de galerij.
          </Typography>
        )}
      </Container>
    </Section>
  );
};
